/**
 * Export Button Component
 *
 * Download data as CSV (Excel-compatible)
 */

import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'

interface ExportButtonProps {
  module: 'personnel' | 'financial' | 'health' | 'vulnerable' | 'population' | 'rehab' | 'strategic'
  year: number
  districtId?: number | null
  label?: string
  className?: string
}

export function ExportButton({
  module,
  year,
  districtId,
  label = 'Export CSV',
  className = '',
}: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    setIsExporting(true)

    try {
      // Build export URL
      const params = new URLSearchParams({
        year: year.toString(),
      })

      if (districtId) {
        params.append('district_id', districtId.toString())
      }

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'
      const url = `${apiBaseUrl}/export/${module}?${params.toString()}`

      // Get auth token from localStorage
      const token = localStorage.getItem('access_token')

      // Fetch file
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Export failed')
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
      const filename = filenameMatch?.[1] || `${module}_export_${year}.csv`

      // Download file
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Export error:', error)
      alert('ไม่สามารถ export ข้อมูลได้ กรุณาลองอีกครั้ง')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className={`inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {isExporting ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>กำลัง export...</span>
        </>
      ) : (
        <>
          <Download className="w-4 h-4" />
          <span>{label}</span>
        </>
      )}
    </button>
  )
}
